import {
  Datagrid,
  List,
  Create,
  Edit,
  TextField,
  TextInput,
  NumberInput,
  ArrayInput,
  SimpleFormIterator,
  ReferenceInput,
  SelectInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  Show,
  SimpleShowLayout,
  ArrayField,
  ReferenceField,
  NumberField,
  SingleFieldList,
  ReferenceArrayField,
  ChipField,
  usePermissions,
  TopToolbar,
  CreateButton,
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import { TabbedForm } from '../components/TabbedForm';
import { ArchivedSelect, FullNameField } from '../components/selects';
import { useCourseIds } from '../utils/useCourseIds';
import { hasRole, type Permissions } from '../types';
import {
  validateCourseAssessmentRow,
  validateCourseDayIndex,
} from './courses-helpers';

const CourseListActions = () => {
  const { permissions } = usePermissions<Permissions>();
  return (
    <TopToolbar>
      {hasRole(permissions, 'tenant_admin') && (
        <CreateButton />
      )}
    </TopToolbar>
  );
};

const CourseList = () => {
  const course_ids = useCourseIds();
  const { permissions } = usePermissions<Permissions>();

  return (
    <List
      exporter={false}
      filter={course_ids ? { id: course_ids } : undefined}
      filters={[<ArchivedSelect key="archived" />]}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      actions={<CourseListActions />}
      empty={
        <Box p={3}>
          <Typography>No courses yet.</Typography>
        </Box>
      }
    >
      <Datagrid
        rowClick={hasRole(permissions, 'tenant_admin') ? 'edit' : 'show'}
      >
        <TextField label="Course Name" source="name" />
      </Datagrid>
    </List>
  );
};

const CourseShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="name" />
      <ArrayField source="assessments">
        <Datagrid bulkActionButtons={false}>
          <ReferenceField
            label="Assessment"
            source="assessment_id"
            reference="assessments"
          >
            <TextField source="name" />
          </ReferenceField>
          <NumberField label="Scheduled Day" source="day_index" />
        </Datagrid>
      </ArrayField>
      <ReferenceArrayField
        label="Instructors"
        source="instructor_ids"
        reference="users"
      >
        <SingleFieldList>
          <FullNameField />
        </SingleFieldList>
      </ReferenceArrayField>
      <ReferenceArrayField
        label="Assignments"
        source="assignment_ids"
        reference="assignments"
      >
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
    </SimpleShowLayout>
  </Show>
);

const CourseCreate = () => (
  <Create>
    <CourseForm />
  </Create>
);

const CourseEdit = () => (
  <Edit mutationMode="pessimistic">
    <CourseForm />
  </Edit>
);

const CourseForm = () => (
  <TabbedForm labels={['Basic', 'Staff', 'Assignments']} warnWhenUnsavedChanges>
    <BasicForm />
    <StaffForm />
    <AssignmentForm />
  </TabbedForm>
);

const BasicForm = () => (
  <Box>
    <TextInput fullWidth label="Course Name" source="name" />
    <ArrayInput source="assessments">
      <SimpleFormIterator inline>
        <ReferenceInput
          perPage={100}
          label="Assessment"
          source="assessment_id"
          reference="assessments"
        >
          <SelectInput
            optionText="name"
            sx={{ minWidth: 300 }}
            validate={validateCourseAssessmentRow}
          />
        </ReferenceInput>
        <NumberInput
          label="Scheduled Day"
          source="day_index"
          validate={validateCourseDayIndex}
        />
      </SimpleFormIterator>
    </ArrayInput>
  </Box>
);

const StaffForm = () => (
  <Box>
    <ReferenceArrayInput
      label="Instructors"
      source="instructor_ids"
      sort={{ field: 'email', order: 'ASC' }}
      reference="users"
      perPage={100}
    >
      <AutocompleteArrayInput optionText="email" />
    </ReferenceArrayInput>
  </Box>
);

const AssignmentForm = () => (
  <Box>
    <ReferenceArrayInput
      sort={{ field: 'name', order: 'ASC' }}
      label="Assignments"
      source="assignment_ids"
      reference="assignments"
      perPage={100}
    >
      <AutocompleteArrayInput optionText="name" />
    </ReferenceArrayInput>
  </Box>
);

export const courses = {
  list: CourseList,
  show: CourseShow,
  create: CourseCreate,
  edit: CourseEdit,
};
